/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AccountModel } from '../models';
import { ApiService, AuthStoreService } from '../../../../core/services';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { Observable, Subject, Subscriber } from 'rxjs';
import { OrganisationsApiEnum } from '../enums/api';
import { TierEnum } from '../enums/api/tier.enum';

@Injectable()
export class OrganisationsApiService {
    /**
     * Emit when get organisation members is called.
     */
    public emitMemberResponse: Subject<any> = new Subject<any>();

    /**
     * Emit when get organisation details is called.
     */
    public emitOrganisationResponse: Subject<any> = new Subject<any>();

    constructor(
        private apiService: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * Create an organisation via the API
     * @param id
     * @param memberObject
     * @param options
     */
    public inviteMember(id: string, memberObject: any, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.post(OrganisationsApiEnum.POST_ORGANISATIONS_INVITE(id), memberObject, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get retrieves organisation members.
     * @param id
     * @param options
     */
    public getMembers(id: string, options?: any): Observable<AccountModel[]> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.get(OrganisationsApiEnum.GET_ORGANISATIONS_MEMBERS(id), moduleAuth)
                .subscribe({
                    next: (data: AccountModel[]): void => {
                        this.emitMemberResponse.next(data);
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get retrieves organisation members.
     * @param id
     * @param userId
     * @param options
     */
    public getMember(id: string, userId: string, options?: any): Observable<AccountModel[]> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.get(OrganisationsApiEnum.GET_ORGANISATIONS_MEMBERS_BY_ID(id, userId), moduleAuth)
                .subscribe({
                    next: (data: AccountModel[]): void => {
                        this.emitMemberResponse.next(data);
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Delete member association with an organisation.
     * @param id
     * @param userId
     * @param options
     */
    public deleteMemberAssociation(id: string, userId: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.delete(OrganisationsApiEnum.DELETE_MEMBER_ASSOCIATION(id, userId), moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get retrieves specific organisation data
     * @param orgId
     * @param options
     */
    public get(orgId: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.get(OrganisationsApiEnum.GET_ORGANISATIONS(orgId), moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        if (!data?.tierID) {
                            const error = {
                                message: 'TierID is missing'
                            };
                            observer.error(error);
                            observer.complete();
                        } else {
                            data.tier = TierEnum[data.tierID]?.toLowerCase() || 'free';
                            this.emitOrganisationResponse.next(data);
                            observer.next(data);
                            observer.complete();
                        }
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Create an organisation via the API
     * @param organisationObject
     * @param options
     */
    public create(organisationObject: any, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.post(OrganisationsApiEnum.POST_ORGANISATIONS_ADD, organisationObject, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Edit an organisation by id via the API
     * @param id
     * @param organisationObject
     * @param options
     */
    public update(id: string, organisationObject: any, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.patch(OrganisationsApiEnum.PATCH_ORGANISATIONS_UPDATE(id), organisationObject, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set up SAML by organisation ID.
     * @param id
     * @param metaData
     * @param options
     */
    public setUpSaml(id: string, metaData: any, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.post(OrganisationsApiEnum.POST_ORGANISATION_SAML(id), metaData, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Delete member association with an organisation.
     * @param id
     * @param options
     */
    public deleteSamlConfig(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.delete(OrganisationsApiEnum.DELETE_ORGANISATION_SAML(id), moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get retrieves specific organisation data
     * @param orgId
     * @param userId
     * @param options
     */
    public getAccount(orgId: string, userId: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.get(OrganisationsApiEnum.GET_ORGANISATIONS_ACCOUNT_BY_ID(orgId, userId), moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Update accounts (edit permissions)
     * @param orgId
     * @param userObject
     * @param options
     */
    public updateAccount(orgId: string, userObject: any, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            // Get our selected module details
            const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.CENTRALISEDMANAGEMENT);

            this.apiService.patch(OrganisationsApiEnum.PATCH_ORGANISATIONS_ACCOUNT_BY_ID(orgId, userObject.id), userObject, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }
}
